import './Started.scss';

import { Alert, Confirm, CustomDialog, Prompt } from '../../Modal';
import {
    AlertExampleCode,
    AllCSSCode,
    ConfirmExampleCode,
    CustomExampleCode,
    CustomStaticCode,
    PromptExampleCode,
    UICode,
} from '../../Code';
import React, { useState } from 'react';

import { CustomDialogContent } from '../ExampleBlock/ExampleBlock';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

type DemoBlockProps = {
    children?: React.ReactNode;
    code?: string;
    onPlay?: () => void;
};

function DemoBlock(props: DemoBlockProps): JSX.Element {
    const [isSource, setIsSource] = useState(false);

    return (
        <div className={['demoBlock', isSource ? '--source' : ''].join(' ')}>
            <div>
                <div className="demoBlock__data">
                    <div>{props.children}</div>
                </div>
                {props.code && (
                    <>
                        <button
                            className="demoBlock__action"
                            onClick={() => {
                                setIsSource(!isSource);
                            }}
                        >
                            {isSource ? 'DOCS' : 'SOURCE'}
                        </button>

                        <div className="demoBlock__source">
                            <div className="demoBlock__code">
                                <SyntaxHighlighter language="tsx" style={prism}>
                                    {props.code}
                                </SyntaxHighlighter>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {props.onPlay && (
                <div className="demoBlock__footer">
                    <PlayButton
                        onClick={() => {
                            props.onPlay && props.onPlay();
                        }}
                    />
                </div>
            )}
        </div>
    );
}

export function Started(): JSX.Element {
    return (
        <main className="started">
            <h1 className="started__header">React St Modal</h1>
            <h2>Overview</h2>
            <div>
                To implement the functionality of modal dialogs this library exports four functions
                and one react component. <br />
                Functions <code>Alert</code>, <code>Confirm</code>, <code>Prompt</code> implement
                the behavior of existing browser functions.
                <br />
                Function <code>CustomDialog</code> shows any JSX element in a modal window.
                <br />
                React component <code>StaticDialog</code> is used to define modals in your JSX
                element.
            </div>

            <h2>Installation</h2>
            <div>You can install the latest version using npm:</div>

            <SyntaxHighlighter language="bash" style={prism}>
                npm install react-st-modal
            </SyntaxHighlighter>

            <h2>Interaction: (Alert, Prompt, Confirm)</h2>
            <div>All interaction functions are async.</div>

            <DemoBlock
                code={AlertExampleCode}
                onPlay={() => {
                    Alert('Alert text', 'Alert title');
                }}
            >
                <div className="demoContent">
                    <span className="funcName">Alert</span> - shows a message (body) and waits for
                    the user to press button.
                </div>
                <div className="demoContent">
                    <span className="demoTitle">Parameters</span>
                    <ul className="demoList">
                        <li>
                            <span className="funcName">body</span>: <i>JSX.Element | string</i>
                        </li>
                        <li>
                            <span className="funcName">title?</span>: <i>string</i>
                        </li>
                        <li>
                            <span className="funcName">buttonText?</span>: <i>string</i> (default:
                            'Ok')
                        </li>
                    </ul>
                </div>
                <div className="demoContent">
                    <span className="demoTitle">Return type:</span> <i>void</i>
                </div>
            </DemoBlock>

            <DemoBlock
                code={ConfirmExampleCode}
                onPlay={async () => {
                    await Confirm('Сonfirmation text', 'Сonfirmation title');
                }}
            >
                <div className="demoContent">
                    <span className="funcName">Confirm</span> - shows a modal window with a text
                    (body) and two buttons: OK and Cancel. The result is true if OK is pressed and
                    false otherwise
                </div>
                <div className="demoContent">
                    <span className="demoTitle">Parameters</span>
                    <ul className="demoList">
                        <li>
                            <span className="funcName">body</span>: <i>JSX.Element | string</i>
                        </li>
                        <li>
                            <span className="funcName">title?</span>: <i>string</i>
                        </li>
                        <li>
                            <span className="funcName">okButtonText?</span>: <i>string</i> (default:
                            'Ok')
                        </li>
                        <li>
                            <span className="funcName">cancelButtonText?</span>: <i>string</i>{' '}
                            (default: 'Cancel')
                        </li>
                    </ul>
                </div>
                <div className="demoContent">
                    <span className="demoTitle">Return type:</span> <i>boolean</i>
                </div>
            </DemoBlock>

            <DemoBlock
                code={PromptExampleCode}
                onPlay={async () => {
                    const age = await Prompt('How old are you?', {
                        isRequired: true,
                        defaultValue: 100,
                    });

                    if (age) {
                        Alert(`You are ${age} years old!`, 'Your age');
                    }
                }}
            >
                <div className="demoContent">
                    <span className="funcName">Prompt</span> - shows a modal window with a text
                    message, an input field for the visitor, and the buttons OK/Cancel
                </div>
                <div className="demoContent">
                    <span className="demoTitle">Parameters</span>
                    <ul className="demoList">
                        <li>
                            <span className="funcName">title?</span>: <i>string</i>
                        </li>
                        <li>
                            <span className="funcName">options?</span>:
                            <ul className="demoList">
                                <li>
                                    <span className="funcName">defaultValue?</span>:
                                    <i>string | number</i>
                                </li>
                                <li>
                                    <span className="funcName">isRequired?</span>: <i>boolean</i>
                                </li>
                                <li>
                                    <span className="funcName">errorText?</span>: <i>string</i>
                                </li>
                                <li>
                                    <span className="funcName">okButtonText?</span>: <i>string</i>
                                </li>
                                <li>
                                    <span className="funcName">cancelButtonText?</span>:
                                    <i>string</i>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>
                <div className="demoContent">
                    <span className="demoTitle">Return type:</span> <i>boolean</i>
                </div>
            </DemoBlock>

            <h2>Custom Dialog</h2>
            <DemoBlock
                code={CustomExampleCode}
                onPlay={async () => {
                    const result = await CustomDialog<string>(<CustomDialogContent />, {
                        title: 'Custom Dialog',
                        showCloseIcon: true,
                    });
                }}
            >
                <div className="demoContent">
                    <span className="funcName">CustomDialog{'<T>'}</span> - is an async function
                    that shows any element in a modal window
                </div>
                <div className="demoContent">
                    <span className="demoTitle">Parameters</span>
                    <ul className="demoList">
                        <li>
                            <span className="funcName">body?</span>: <i>string</i>
                        </li>
                        <li>
                            <span className="funcName">options?</span>:
                            <ul className="demoList">
                                <li>
                                    <span className="funcName">title?</span>: <i>string</i> - modal
                                    dialog title
                                </li>
                                <li>
                                    <span className="funcName">className?</span>: <i>string</i> -
                                    css className
                                </li>
                                <li>
                                    <span className="funcName">isFocusLock?</span>: <i>boolean</i> -
                                    (default: true), lock focus on modal
                                </li>
                                <li>
                                    <span className="funcName">defaultBodyOverflow?</span>:{' '}
                                    <i>string</i> - (default: 'visible'), default value to body css
                                    property overflow
                                </li>
                                <li>
                                    <span className="funcName">showCloseIcon?</span>: <i>boolean</i>{' '}
                                    - (default: false), show close button in the top corner of the
                                    window
                                </li>
                                <li>
                                    <span className="funcName">isCanClose?</span>: <i>boolean</i> -
                                    (default: true), is it possible to close the dialog by clicking
                                    on the overlay or ESC button
                                </li>
                                <li>
                                    <span className="funcName">isBodyScrollLocked?</span>:{' '}
                                    <i>boolean</i> (default: true), content scrolling lock
                                </li>
                                <li>
                                    <span className="funcName">replaceScrollBar?</span>:{' '}
                                    <i>boolean</i> - (default: true), whether to replace the body
                                    scrollbar with a placeholder
                                </li>
                                <li>
                                    <span className="funcName">scrollBarPlaceholderColor?</span>:{' '}
                                    <i>string</i> - (default: '#eeeeee'), default color for the
                                    scrollbar placeholder
                                </li>
                                <li>
                                    <span className="funcName">onAfterOpen?</span>:{' '}
                                    <i>() ={'>'} void</i> - event called after the dialog was opened
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>
                <div className="demoContent">
                    <span className="funcName">{'useDialog<T>'}</span> - hook to control a dialog
                    from a child element.
                    <br />
                    <span className="demoTitle">Returns</span>
                    <ul className="demoList">
                        <li>
                            <span className="funcName">isOpen</span>: <i>boolean</i> - the current
                            state of the modal
                        </li>
                        <li>
                            <span className="funcName">close</span>: <i>{'(result?: T) => void'}</i>{' '}
                            - function that closes the dialog and returns the result
                        </li>
                    </ul>
                </div>
                <div className="demoContent">
                    <span className="demoTitle">Return type:</span> <i>T</i>
                </div>
            </DemoBlock>

            <h2>Static Dialog</h2>
            <DemoBlock code={CustomStaticCode}>
                <div className="demoContent">
                    <span className="funcName">StaticDialog</span> - it is a React component that
                    used to define modals in your JSX element
                </div>
                <div className="demoContent">
                    <span className="demoTitle">Props</span>
                    <ul className="demoList">
                        <li>
                            <span className="funcName">isOpen?</span>: <i>boolean</i> - describing
                            if the modal should be shown or not
                        </li>
                        <li>
                            <span className="funcName">children</span>: <i>React.ReactNode</i> - the
                            element shown in the modal dialog
                        </li>
                        <li>
                            <span className="funcName">title?</span>: <i>string</i> - modal dialog
                            title
                        </li>
                        <li>
                            <span className="funcName">className?</span>: <i>string</i> - css
                            className
                        </li>
                        <li>
                            <span className="funcName">isFocusLock?</span>: <i>boolean</i> -
                            (default: true), lock focus on modal
                        </li>
                        <li>
                            <span className="funcName">defaultBodyOverflow?</span>: <i>string</i> -
                            (default: 'visible'), default value to body css property overflow
                        </li>
                        <li>
                            <span className="funcName">showCloseIcon?</span>: <i>boolean</i> -
                            (default: false), show close button in the top corner of the window
                        </li>
                        <li>
                            <span className="funcName">isCanClose?</span>: <i>boolean</i> -
                            (default: true), is it possible to close the dialog by clicking on the
                            overlay or ESC button
                        </li>
                        <li>
                            <span className="funcName">isBodyScrollLocked?</span>: <i>boolean</i>{' '}
                            (default: true), content scrolling lock
                        </li>
                        <li>
                            <span className="funcName">replaceScrollBar?</span>: <i>boolean</i> -
                            (default: true), whether to replace the body scrollbar with a
                            placeholder
                        </li>
                        <li>
                            <span className="funcName">scrollBarPlaceholderColor?</span>:{' '}
                            <i>string</i> - (default: '#eeeeee'), default color for the scrollbar
                            placeholder
                        </li>
                        <li>
                            <span className="funcName">onAfterClose{'<T>'}?</span>:{' '}
                            <i>
                                ({'result?: T'}) ={'>'} void
                            </i>{' '}
                            - event called after the dialog was closed
                        </li>
                        <li>
                            <span className="funcName">onAfterOpen?</span>: <i>() ={'>'} void</i> -
                            event called after the dialog was opened
                        </li>
                    </ul>
                </div>
                <div className="demoContent">
                    <span className="demoTitle">Return type:</span> <i>JSX.Element</i>
                </div>
            </DemoBlock>

            <h2>UI Elements</h2>
            <DemoBlock code={UICode}>
                <div className="demoContent">
                    To decorate your dialogs, you can use the following components:
                    <span className="funcName"> ModalButton</span>,
                    <span className="funcName"> ModalContent</span>,
                    <span className="funcName"> ModalFooter</span>
                </div>
            </DemoBlock>

            <h2>CSS</h2>
            <DemoBlock code={AllCSSCode}>
                <div className="demoContent">
                    To customize the look of the UI, you can use the following (click on "source")
                    CSS variables.
                </div>
            </DemoBlock>
        </main>
    );
}

type PlayButtonProps = {
    onClick?: () => void;
};

export function PlayButton(props: PlayButtonProps): JSX.Element {
    return (
        <svg
            enableBackground="new 0 0 128 128"
            height="128px"
            version="1.1"
            viewBox="0 0 128 128"
            width="128px"
            onClick={() => {
                props.onClick && props.onClick();
            }}
        >
            <path
                d="M107.574,55.165L38.623,15.364C37.044,14.452,35.287,14,33.524,14c-1.762,0-3.519,0.453-5.096,1.365  c-3.157,1.82-5.098,5.186-5.098,8.831v79.611c0,3.644,1.94,7.008,5.098,8.83c1.577,0.912,3.334,1.363,5.096,1.363  c1.763,0,3.52-0.451,5.099-1.363l68.951-39.811c3.153-1.822,5.095-5.186,5.095-8.832C112.669,60.351,110.728,56.987,107.574,55.165z  "
                fill="#FF6D72"
            />
        </svg>
    );
}
